import { useState, useEffect } from 'react';
import { Info } from '@/components/icons';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/beui';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './dialog';
import type { DiagramMetadata, Folder } from '../../types';

interface DiagramSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  diagram: DiagramMetadata | null;
  onUpdate: (id: string, data: { name?: string; description?: string }) => void;
  folders: Folder[];
  onMoveToFolder: (diagramId: string, folderId: string | null) => void;
}

export default function DiagramSettingsModal({
  isOpen,
  onClose,
  diagram,
  onUpdate,
  folders,
  onMoveToFolder,
}: DiagramSettingsModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && diagram) {
      setName(diagram.name);
      setDescription(diagram.description || '');
      setSelectedFolderId(diagram.folderId || '');
    }
  }, [isOpen, diagram]);

  const handleSave = async () => {
    if (!diagram) return;
    setIsSaving(true);
    try {
      const updates: { name?: string; description?: string } = {};

      if (name !== diagram.name) {
        updates.name = name;
      }

      const currentDescription = diagram.description || '';
      if (description !== currentDescription) {
        updates.description = description || '';
      }

      // Handle folder move
      const currentFolderId = diagram.folderId || '';
      if (selectedFolderId !== currentFolderId) {
        await onMoveToFolder(diagram.id, selectedFolderId || null);
      }

      if (Object.keys(updates).length > 0) {
        await onUpdate(diagram.id, updates);
      }

      onClose();
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md gap-0 overflow-visible p-0 bg-card dark:bg-card border-border dark:border-border rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border dark:border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted dark:bg-card">
              <Info className="w-5 h-5 text-muted-foreground dark:text-muted-foreground" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-foreground dark:text-foreground">
                Diagram Settings
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground dark:text-muted-foreground truncate max-w-[200px]">
                {diagram?.name}
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground dark:text-muted-foreground">
              Diagram Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2.5 text-sm bg-muted dark:bg-card border border-border dark:border-border rounded-lg text-foreground dark:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter diagram name"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground dark:text-muted-foreground">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2.5 text-sm bg-muted dark:bg-card border border-border dark:border-border rounded-lg text-foreground dark:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              placeholder="Add a description (optional)"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground dark:text-muted-foreground">
              Folder
            </label>
            <Select
              value={selectedFolderId || ''}
              onValueChange={(v) => setSelectedFolderId(v || null)}
              radius={10}
              className="w-full"
            >
              <SelectTrigger className="rounded-lg bg-muted dark:bg-card px-3 py-2.5">
                <SelectValue placeholder="No folder" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">No folder</SelectItem>
                {folders.map((folder) => (
                  <SelectItem key={folder.id} value={folder.id}>
                    {folder.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="px-6 py-4 bg-muted dark:bg-card/50 border-t border-border dark:border-border flex justify-end gap-3 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-foreground dark:text-muted-foreground hover:bg-muted dark:hover:bg-muted rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary rounded-lg transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
